import {
  Button, Flex, HStack, InfoBox, Input, Modal, NumberInput, Text, Textarea,
  VStack
} from 'components/ui';
import { useTranslation } from 'hooks/useTranslation';
import { useEffect, useState } from 'react';
import { MdClose, MdPowerSettingsNew } from 'react-icons/md';
import { updateMaskingRule, UpdateMaskingRuleDto } from 'services/maskingService';

interface MaskingRule {
  id: string;
  name: string;
  description?: string;
  fieldPath: string;
  keepFirstChars: number;
  keepLastChars: number;
  maskingSymbol: string;
  isActive: boolean;
  priority: number;
}

interface EditMaskingRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  rule: MaskingRule;
  onSuccess: () => void;
}

const EditMaskingRuleModal = ({
  isOpen,
  onClose,
  rule,
  onSuccess,
}: EditMaskingRuleModalProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState<UpdateMaskingRuleDto>({});

  useEffect(() => {
    if (isOpen && rule) {
      setFormData({
        name: rule.name,
        description: rule.description,
        fieldPath: rule.fieldPath,
        keepFirstChars: rule.keepFirstChars,
        keepLastChars: rule.keepLastChars,
        maskingSymbol: rule.maskingSymbol,
        isActive: rule.isActive,
        priority: rule.priority,
      });
    }
  }, [isOpen, rule]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await updateMaskingRule(rule.id, formData);
      setSuccess(t('app.security.editMaskingRuleModal.success'));
      setTimeout(() => {
        setSuccess('');
        onSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || t('app.security.editMaskingRuleModal.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <Flex justify="space-between" align="center">
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'var(--modal-header-text)'
          }}>
            {t('app.security.editMaskingRuleModal.title')}
          </h3>
          <Modal.CloseTrigger asChild>
            <button style={{
              color: 'var(--modal-close-icon)',
              transition: 'color 0.2s'
            }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--modal-close-icon-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--modal-close-icon)'}>
              <MdClose style={{ height: '1.5rem', width: '1.5rem' }} />
            </button>
          </Modal.CloseTrigger>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <InfoBox variant="error" mb={4}>
            <HStack justify="space-between">
              <Text color="red.700">{error}</Text>
              <Button onClick={() => setError('')} size="sm" variant="ghost">
                {t('app.security.editMaskingRuleModal.dismiss')}
              </Button>
            </HStack>
          </InfoBox>
        )}

        {success && (
          <InfoBox variant="success" mb={4}>
            <Text color="green.700">{success}</Text>
          </InfoBox>
        )}

        <Button
          onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
          variant='outline'
          colorPalette={formData.isActive ? 'gray' : 'green'}
          size="sm"
          mb={4}
          width="full"
        >
          <MdPowerSettingsNew />
          {formData.isActive ? t('app.security.editMaskingRuleModal.disable') : t('app.security.editMaskingRuleModal.enable')}
        </Button>

        <VStack gap={4}>
          <Input
            label={t('app.security.editMaskingRuleModal.nameLabel')}
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <Textarea
            label={t('app.security.editMaskingRuleModal.descriptionLabel')}
            value={formData.description || ''}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={2}
          />

          <Input
            label={t('app.security.editMaskingRuleModal.fieldPathLabel')}
            value={formData.fieldPath || ''}
            onChange={(e) =>
              setFormData({ ...formData, fieldPath: e.target.value })
            }
            helperText={t('app.security.editMaskingRuleModal.fieldPathHelper')}
          />

          <NumberInput
            label={t('app.security.editMaskingRuleModal.keepFirstLabel')}
            value={formData.keepFirstChars}
            onValueChange={(val: number) =>
              setFormData({ ...formData, keepFirstChars: val })
            }
            min={0}
            max={50}
          />

          <NumberInput
            label={t('app.security.editMaskingRuleModal.keepLastLabel')}
            value={formData.keepLastChars}
            onValueChange={(val: number) =>
              setFormData({ ...formData, keepLastChars: val })
            }
            min={0}
            max={50}
          />

          <Input
            label={t('app.security.editMaskingRuleModal.maskingSymbolLabel')}
            value={formData.maskingSymbol || ''}
            onChange={(e) =>
              setFormData({ ...formData, maskingSymbol: e.target.value })
            }
            maxLength={1}
            helperText={t('app.security.editMaskingRuleModal.maskingSymbolHelper')}
          />

          <NumberInput
            label={t('app.security.editMaskingRuleModal.priorityLabel')}
            value={formData.priority}
            onValueChange={(val: number) => setFormData({ ...formData, priority: val })}
            min={0}
            max={100}
            helperText={t('app.security.editMaskingRuleModal.priorityHelper')}
          />
        </VStack>
      </Modal.Body>
      <Modal.Footer>
        <Flex gap={3}>
          <Button variant="ghost" onClick={onClose}>
            {t('app.security.editMaskingRuleModal.cancel')}
          </Button>
          <Button colorPalette="blue" onClick={handleSubmit} loading={loading}>
            {t('app.security.editMaskingRuleModal.update')}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};

export default EditMaskingRuleModal;
